/**
 * Vehicle Limit Configuration
 *
 * Bestimmt das Fahrzeuglimit basierend auf dem in Firestore gespeicherten Wert.
 * Der Wert wird manuell in der Datenbank (appSettings/general → vehicleLimit) gesetzt.
 *
 * Tier-Zuordnung:
 *   vehicleLimit === 5   → Basic
 *   vehicleLimit === 10  → Standard
 *   vehicleLimit > 10    → Pro (benutzerdefiniert)
 *   kein Eintrag          → Standard (Fallback: 10)
 */

// License tier definitions
export const LICENSE_TIERS = {
  BASIC: 'basic',
  STANDARD: 'standard',
  PRO: 'pro',
};

// Vehicle limits for fixed tiers
export const TIER_LIMITS = {
  [LICENSE_TIERS.BASIC]: 5,
  [LICENSE_TIERS.STANDARD]: 10,
};

// Default wenn kein Wert in der Datenbank vorhanden
export const DEFAULT_VEHICLE_LIMIT = TIER_LIMITS[LICENSE_TIERS.STANDARD];

/**
 * Bestimmt das Tier anhand des vehicleLimit-Werts
 * @param {number|undefined} vehicleLimit - Der Wert aus der Datenbank
 * @returns {string} - Das zugeordnete Tier
 */
export const determineTier = (vehicleLimit) => {
  const limit = vehicleLimit ?? DEFAULT_VEHICLE_LIMIT;

  if (limit <= TIER_LIMITS[LICENSE_TIERS.BASIC]) return LICENSE_TIERS.BASIC;
  if (limit <= TIER_LIMITS[LICENSE_TIERS.STANDARD]) return LICENSE_TIERS.STANDARD;
  return LICENSE_TIERS.PRO;
};

/**
 * Prüft ob ein weiteres Fahrzeug hinzugefügt werden darf
 * @param {number} currentVehicleCount - Aktuelle Anzahl der Fahrzeuge
 * @param {number|undefined} vehicleLimit - Der Wert aus der Datenbank
 * @returns {boolean}
 */
export const canAddVehicle = (currentVehicleCount, vehicleLimit) => {
  const limit = vehicleLimit ?? DEFAULT_VEHICLE_LIMIT;
  return currentVehicleCount < limit;
};

/**
 * Gibt das effektive Fahrzeuglimit zurück
 * @param {number|undefined} vehicleLimit - Der Wert aus der Datenbank
 * @returns {number}
 */
export const getVehicleLimit = (vehicleLimit) => {
  return vehicleLimit ?? DEFAULT_VEHICLE_LIMIT;
};

/**
 * Gibt Lizenzinformationen für die UI zurück
 * @param {number|undefined} vehicleLimit - Der Wert aus der Datenbank
 * @returns {{ tier: string, limit: number, tierName: string }}
 */
export const getLicenseInfo = (vehicleLimit) => {
  const limit = getVehicleLimit(vehicleLimit);
  const tier = determineTier(limit);

  const tierNames = {
    [LICENSE_TIERS.BASIC]: 'Basic',
    [LICENSE_TIERS.STANDARD]: 'Standard',
    [LICENSE_TIERS.PRO]: 'Pro',
  };

  return {
    tier,
    limit,
    tierName: tierNames[tier],
  };
};

const vehicleLimitConfig = {
  canAddVehicle,
  getVehicleLimit,
  getLicenseInfo,
  determineTier,
  LICENSE_TIERS,
  TIER_LIMITS,
  DEFAULT_VEHICLE_LIMIT,
};

export default vehicleLimitConfig;
